import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../config';

function getAuthHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function uploadPhoto({ file, photoType, visibility, reservationId, groupId, caption }) {
  // Step 1: get presigned URL
  const presignRes = await axios.post(
    `${API_URL}/api/uploads/presign`,
    {
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      photoType,
      visibility: visibility || undefined,
      reservationId: reservationId || undefined,
      groupId: groupId || undefined,
    },
    { headers: getAuthHeader() }
  );
  const { uploadUrl, key, publicUrl } = presignRes.data.data;

  // Step 2: PUT directly to S3 (native fetch — axios adds headers that break presigned URLs)
  const s3Res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!s3Res.ok) {
    const text = await s3Res.text();
    throw new Error(`S3 upload failed (${s3Res.status}): ${text}`);
  }

  // Step 3: confirm and save metadata
  const confirmRes = await axios.post(
    `${API_URL}/api/uploads/confirm`,
    {
      key,
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      photoType,
      visibility: visibility || undefined,
      reservationId: reservationId || undefined,
      groupId: groupId || undefined,
      caption: caption || undefined,
    },
    { headers: getAuthHeader() }
  );

  return { photo: confirmRes.data.data.photo, publicUrl };
}

async function deletePhoto(id) {
  await axios.delete(`${API_URL}/api/uploads/${id}`, { headers: getAuthHeader() });
}

async function fetchReservationPhotos(reservationId) {
  const res = await axios.get(`${API_URL}/api/uploads/reservation/${reservationId}`, {
    headers: getAuthHeader(),
  });
  return res.data.data;
}

async function fetchGalleryPhotos({ visibility, page = 1, limit = 20 } = {}) {
  const params = new URLSearchParams();
  if (visibility) params.set('visibility', visibility);
  params.set('page', String(page));
  params.set('limit', String(limit));
  const res = await axios.get(`${API_URL}/api/uploads/gallery?${params}`, {
    headers: getAuthHeader(),
  });
  return res.data.data;
}

export function useUploadPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadPhoto,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['gallery-photos'] });
      if (variables.photoType === 'reservation' && variables.reservationId) {
        queryClient.invalidateQueries({ queryKey: ['reservation-photos', variables.reservationId] });
      }
      if (variables.photoType === 'avatar') {
        queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
    },
  });
}

export function useGalleryPhotos({ visibility, page, limit } = {}) {
  return useQuery({
    queryKey: ['gallery-photos', { visibility, page, limit }],
    queryFn: () => fetchGalleryPhotos({ visibility, page, limit }),
  });
}

export function useReservationPhotos(reservationId) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reservation-photos', reservationId],
    queryFn: () => fetchReservationPhotos(reservationId),
    enabled: !!reservationId,
  });
  return { photos: data || [], isLoading, error };
}

export function useDeletePhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePhoto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-photos'] });
      queryClient.invalidateQueries({ queryKey: ['reservation-photos'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}
